/**
 * Guardia "modifica condizioni economiche post-conferma" — tipi e parsing.
 *
 * Stesso pattern di `modificaFiscale.ts` (R6-B): il backend risponde 409 con
 * detail STRUTTURATO quando l'ordine ha un preventivo FIRMATO vivo — le nuove
 * condizioni (tariffa e/o trasporto) contraddirebbero il documento
 * contrattuale. Il detail elenca SOLO i blocchi che cambiano davvero. Su
 * conferma si rinvia la stessa PATCH con `conferma_modifica_condizioni: true`.
 *
 * La discriminante è il campo `code`, mai il testo: l'endpoint ha anche 409
 * testuali ("Transizione non valida da stato ...") per gli stati non ammessi,
 * che NON vanno intercettati dalla modale di conferma.
 */

/** Riferimento al preventivo firmato che motiva la conferma. */
export interface PreventivoRiferimento {
  firmato_caricato_at: string | null;
  confermato_at: string | null;
}

/** Blocco tariffa del 409 — presente solo se la tariffa cambia. */
export interface CondizioniTariffaDelta {
  attuale: number | null;
  nuova: number;
}

/** Stato trasporto (attuale o nuovo) nel 409. */
export interface CondizioniTrasportoStato {
  abilitato: boolean;
  costo_per_viaggio: number | null;
}

/** Blocco trasporto del 409 — presente solo se il trasporto cambia. */
export interface CondizioniTrasportoDelta {
  attuale: CondizioniTrasportoStato;
  nuovo: CondizioniTrasportoStato;
}

/** Body del 409 di conferma modifica condizioni economiche. */
export interface ConfermaModificaCondizioniDetail {
  code: 'conferma_modifica_condizioni_richiesta';
  message: string;
  tariffa: CondizioniTariffaDelta | null;
  trasporto: CondizioniTrasportoDelta | null;
  preventivo: PreventivoRiferimento | null;
}

/**
 * Riconosce il 409 di conferma modifica condizioni e ne estrae il detail.
 * Ritorna null per qualunque altro errore (incluso il 409 testuale di
 * transizione non valida).
 */
export function parseConfermaCondizioni(err: unknown): ConfermaModificaCondizioniDetail | null {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  const d = detail as Partial<ConfermaModificaCondizioniDetail>;
  if (d.code !== 'conferma_modifica_condizioni_richiesta') return null;
  return {
    code: d.code,
    message: d.message ?? '',
    tariffa: d.tariffa && typeof d.tariffa === 'object' ? d.tariffa : null,
    trasporto: d.trasporto && typeof d.trasporto === 'object' ? d.trasporto : null,
    preventivo: d.preventivo && typeof d.preventivo === 'object' ? d.preventivo : null,
  };
}
